import { Injectable, inject } from '@angular/core';
import { buildKcc20MintAvailabilityOperation } from '@kaspacom/kcc20-tx-builder/mint-availability-operation';
import { defer, firstValueFrom } from 'rxjs';
import { WalletManager } from '../../managers/wallet.manager';
import { Kcc20ClientPsktBuilderService } from '../wallet-operations/kcc20-client-pskt-builder.service';
import {
  Kcc20EnqueuedTokenAction,
  Kcc20TokenActionRequest,
  Kcc20WalletOperationQueueService,
} from '../wallet-operations/kcc20-wallet-operation-queue.service';
import { Kcc20WalletOperationResponse } from '../wallet-operations/kcc20-wallet-operation.models';

const MAX_AUTHORITIES_PER_TRANSACTION = 5;

@Injectable({ providedIn: 'root' })
export class Kcc20MintAvailabilityFlowService {
  private readonly queue = inject(Kcc20WalletOperationQueueService);
  private readonly builder = inject(Kcc20ClientPsktBuilderService);
  private readonly walletManager = inject(WalletManager);
  private readonly pending = new Map<
    string,
    { active: boolean; owner: string; flow: Kcc20EnqueuedTokenAction }
  >();

  enqueueActionAndWait(
    request: Pick<Kcc20TokenActionRequest, 'tokenId' | 'title' | 'build'> & {
      active: boolean;
      retry: () => void;
    },
  ): Kcc20EnqueuedTokenAction {
    const tokenId = request.tokenId.toLowerCase();
    const signedWallet = this.walletManager.requireSignedWallet(
      'updating public minting',
    );
    const wallet = {
      walletAddress: signedWallet.walletAddress,
      kcc20Owner: signedWallet.kcc20Owner,
    };
    const existing = this.pending.get(tokenId);
    if (existing) {
      if (
        existing.active === request.active &&
        existing.owner === wallet.walletAddress
      )
        return existing.flow;
      const message = 'Wait for the current public mint update to finish.';
      const id = this.queue.recordPreflightFailure({
        tokenId,
        title: request.title,
        message,
      });
      return {
        id,
        completion: Promise.resolve({
          actionId: id,
          status: 'cancelled',
          message,
        }),
      };
    }
    let plan: Kcc20WalletOperationResponse | undefined;
    let batches: Record<string, unknown>[][] = [];
    let batchIndex = 0;
    const build = () =>
      defer(async () => {
        const current = this.walletManager.requireSignedWallet(
          'updating public minting',
        );
        if (
          current.walletAddress !== wallet.walletAddress ||
          current.kcc20Owner !== wallet.kcc20Owner
        ) {
          throw new Error(
            'Reconnect the original wallet to update public minting.',
          );
        }
        if (!plan) {
          const loaded = await firstValueFrom(request.build());
          const payload = loaded.payload;
          if (
            String(payload.params['covenantId']).toLowerCase() !== tokenId ||
            payload.owner.walletAddress !== wallet.walletAddress ||
            payload.owner.kcc20Owner !== wallet.kcc20Owner
          ) {
            throw new Error(
              'Public mint request does not match the selected token and wallet.',
            );
          }
          const sources = payload.params['activeMinterUtxos'];
          if (
            payload.action !== 'setPublicMintActive' ||
            !Array.isArray(sources) ||
            !sources.length
          ) {
            throw new Error(
              'Public mint authorities are unavailable. Refresh and try again.',
            );
          }
          const active = payload.params['active'];
          if (typeof active !== 'boolean' || active !== request.active)
            throw new Error('Public mint setting is invalid.');
          const seen = new Set<string>();
          const remaining = sources.filter(
            (source: Record<string, unknown>) => {
              const outpoint = `${source['txidHex']}:${source['vout']}`;
              if (seen.has(outpoint))
                throw new Error(
                  'Duplicate public mint authority. Refresh and try again.',
                );
              seen.add(outpoint);
              const state = source['state'] as
                | Record<string, unknown>
                | undefined;
              const extension = state?.['extension'] as
                | Record<string, unknown>
                | undefined;
              const currentlyActive =
                (state?.['publicMintActive'] ??
                  extension?.['publicMintActive']) === true;
              return currentlyActive !== active;
            },
          );
          if (!remaining.length)
            throw new Error('Public minting already has this setting.');
          batches = [];
          for (
            let i = 0;
            i < remaining.length;
            i += MAX_AUTHORITIES_PER_TRANSACTION
          ) {
            batches.push(
              remaining.slice(i, i + MAX_AUTHORITIES_PER_TRANSACTION),
            );
          }
          plan = loaded;
        }
        const batch = batches[batchIndex];
        // Build only when this batch's queue turn starts, so funding comes from
        // the current node UTXO set after the preceding transaction was indexed.
        const operation = buildKcc20MintAvailabilityOperation(
          wallet,
          {
            covenantId: tokenId,
            active: plan.payload.params['active'] as boolean,
            activeMinterUtxo: batch[0],
            activeMinterUtxos: batch,
          },
          { network: plan.payload.network },
        ) as Kcc20WalletOperationResponse;
        return this.builder.build(operation);
      });

    const retry = () => {
      // Retry may run before the failed leaf's completion microtasks drain.
      // Wait for aggregate cleanup before the page creates a replacement flow.
      void completion
        .then(() => {
          const current = this.walletManager.requireSignedWallet(
            'updating public minting',
          );
          if (
            current.walletAddress !== wallet.walletAddress ||
            current.kcc20Owner !== wallet.kcc20Owner
          ) {
            throw new Error(
              'Reconnect the original wallet to update public minting.',
            );
          }
          request.retry();
        })
        .catch((error: unknown) => {
          this.queue.recordPreflightFailure({
            tokenId,
            title: request.title,
            message:
              error instanceof Error
                ? error.message
                : 'Public mint update could not be retried.',
            retry,
          });
        });
    };
    const enqueue = () =>
      this.queue.enqueueActionAndWait({
        tokenId,
        title: request.title,
        build,
        retryFlow: retry,
      });
    const first = enqueue();
    const completion = (async () => {
      let result = await first.completion;
      while (result.status === 'indexed' && batchIndex + 1 < batches.length) {
        batchIndex += 1;
        result = await enqueue().completion;
      }
      return result;
    })().finally(() => this.pending.delete(tokenId));
    const flow = { id: first.id, completion };
    this.pending.set(tokenId, {
      active: request.active,
      owner: wallet.walletAddress,
      flow,
    });
    return flow;
  }
}
